import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AppUseSelector } from '../../redux/hooks'
import {
  APP_ONELINK_URL,
  DESKTOP_PROMO_SEEN_KEY,
  isAppPromoPath,
  isDesktopBrowser,
  readFlag,
  trackAppPromo,
  writeFlag,
} from '../../utils/appPromo'
import { membershipNameType } from '../../utils/constant'
import './appPromo.css'

// Let the page settle first so the toast reads as news, not as part of the page loading.
const SHOW_DELAY_MS = 4000

const CloseIcon = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
  </svg>
)

/**
 * Desktop-only "we have mobile apps" toast, bottom-right. Shown once per browser, ever:
 * the seen flag is written the moment it appears, not when it is dismissed. Clicking it
 * opens a full-screen dialog with a QR code for the phone.
 */
const DesktopAppPromo: React.FC = () => {
  const { t } = useTranslation('dashboard')
  const { pathname } = useLocation()
  const { reminder, membership } = AppUseSelector(({ user }) => user)
  const [toastVisible, setToastVisible] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const onPromoPath = isAppPromoPath(pathname)
  const loggedIn = Boolean(localStorage.getItem('authToken'))
  const isPro = membership?.name === membershipNameType.Pro

  useEffect(() => {
    // `reminder` drives the trial-ending notification in the same corner, which wins.
    if (!loggedIn || !onPromoPath || reminder || toastVisible || readFlag(DESKTOP_PROMO_SEEN_KEY)) return
    if (!isDesktopBrowser()) return

    const timer = window.setTimeout(() => {
      writeFlag(DESKTOP_PROMO_SEEN_KEY)
      setToastVisible(true)
      trackAppPromo('app_promo_shown', 'desktop_toast')
    }, SHOW_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [loggedIn, onPromoPath, reminder, toastVisible])

  useEffect(() => {
    if (!dialogOpen) return
    closeButtonRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDialogOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [dialogOpen])

  const openDialog = () => {
    setToastVisible(false)
    setDialogOpen(true)
    trackAppPromo('app_promo_opened', 'desktop_toast')
  }

  const dismissToast = (event: React.MouseEvent) => {
    event.stopPropagation()
    setToastVisible(false)
    trackAppPromo('app_promo_dismissed', 'desktop_toast')
  }

  return (
    <>
      {toastVisible && onPromoPath && (
        <div
          className='app-promo-toast'
          role='button'
          tabIndex={0}
          onClick={openDialog}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              openDialog()
            }
          }}
        >
          <img className='app-promo-toast__icon' src='/images/app-promo/app-icon.png' alt='' width='40' height='40' />
          <span className='app-promo-toast__body'>
            <strong className='app-promo-toast__title'>{t('appPromo.toastTitle')}</strong>
            <span className='app-promo-toast__text'>
              {isPro ? t('appPromo.toastPro') : t('appPromo.toastFree')}
            </span>
            <span className='app-promo-toast__link'>{t('appPromo.getApp')}</span>
          </span>
          <button type='button' className='app-promo-toast__close' onClick={dismissToast} aria-label={t('appPromo.dismiss')}>
            <CloseIcon />
          </button>
        </div>
      )}

      {dialogOpen && (
        <div className='app-promo-dialog' onClick={() => setDialogOpen(false)}>
          <div
            className='app-promo-dialog__card'
            role='dialog'
            aria-modal='true'
            aria-labelledby='app-promo-dialog-title'
            onClick={(event) => event.stopPropagation()}
          >
            <button
              ref={closeButtonRef}
              type='button'
              className='app-promo-dialog__close'
              onClick={() => setDialogOpen(false)}
              aria-label={t('appPromo.close')}
            >
              <CloseIcon />
            </button>
            <div className='app-promo-dialog__hero'>
              <img src='/images/app-promo/app-screens.jpg' alt='' width='1200' height='321' />
            </div>
            <div className='app-promo-dialog__content'>
              <h2 id='app-promo-dialog-title' className='app-promo-dialog__title'>
                {t('appPromo.dialogTitle')}
              </h2>
              <p className='app-promo-dialog__text'>{t('appPromo.dialogText')}</p>
              <div className='app-promo-dialog__qr'>
                <img src='/images/app-promo/onelink-qr.svg' alt={t('appPromo.qrAlt')} width='144' height='144' />
              </div>
              <span className='app-promo-dialog__caption'>{t('appPromo.scan')}</span>
              <a
                className='app-promo-dialog__install'
                href={APP_ONELINK_URL}
                target='_blank'
                rel='noopener noreferrer'
                onClick={() => trackAppPromo('app_promo_install_clicked', 'desktop_modal')}
              >
                {t('appPromo.install')}
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default DesktopAppPromo
